import React from "react";
import { Link } from "react-router-dom";
import { renderWithSmilies } from "@/components/smilies";

/**
 * Render comment text with both smilies AND clickable @mentions.
 * `mentions` is the array stored on the comment doc: [{user_id, name, picture}, ...].
 * If a @handle in the text matches a mention by name (case-insensitive),
 * it becomes a Link to /u/{user_id}. Otherwise it remains plain @text.
 *
 * We delegate the smilie rendering to renderWithSmilies for each non-mention
 * text chunk so emojis still work inside the same comment.
 */
const MENTION_RE = /(?:^|(?<=\s))@([a-zA-Z0-9_.\-]{2,30})/g;

export default function CommentText({ text, mentions, smilieMap }) {
  if (!text) return null;
  const byName = {};
  for (const m of mentions || []) {
    if (m && m.name) byName[m.name.toLowerCase()] = m;
  }

  // Walk the string, emitting plain text chunks (with smilies) and mention links
  const nodes = [];
  let last = 0;
  let match;
  let key = 0;
  // Reset regex state per call
  MENTION_RE.lastIndex = 0;
  while ((match = MENTION_RE.exec(text)) !== null) {
    const handle = match[1];
    const matchStart = match.index + (match[0].startsWith("@") ? 0 : 1);
    // Push the text before this mention (with smilies)
    if (matchStart > last) {
      const chunk = text.slice(last, matchStart);
      nodes.push(
        <React.Fragment key={`t${key++}`}>
          {renderWithSmilies(chunk, smilieMap)}
        </React.Fragment>,
      );
    }
    const u = byName[handle.toLowerCase()];
    if (u) {
      nodes.push(
        <Link
          key={`m${key++}`}
          to={`/u/${u.user_id}`}
          data-testid={`mention-link-${u.name}`}
          className="text-[var(--tt-primary)] font-semibold hover:underline"
        >
          @{u.name}
        </Link>,
      );
    } else {
      // Unresolved @handle — still highlight visually but no link
      nodes.push(
        <span key={`m${key++}`} className="text-[var(--tt-primary)]/70 font-semibold">
          @{handle}
        </span>,
      );
    }
    last = matchStart + 1 + handle.length;
  }
  if (last < text.length) {
    const chunk = text.slice(last);
    nodes.push(
      <React.Fragment key={`t${key++}`}>
        {renderWithSmilies(chunk, smilieMap)}
      </React.Fragment>,
    );
  }
  return <>{nodes}</>;
}
